/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */

// redux utils
import { createSlice, current } from '@reduxjs/toolkit';
import {
  fetchAccountRevenueShare,
  fetchSiteRevenueShare,
  fetchInvoiceConfig,
  updateAccountRevenueShare,
  updateSiteRevenueShare,
  updateInvoiceConfig,
  fetchLatestProductRevenueDeduction,
  fetchLatestProductRevenueDeductionForAccount,
  updateProductRevenueDeduction,
  updateProductRevenueDeductionForAccount,
  fetchDashboardMessage,
  updateDashboardMessage,
  fetchReportApiKey, createReportApiKey,
} from "../thunks/settings";

import {getBlockListCSV, uploadBlockListCSV} from "../thunks/adServeBlocking";


import {parseCSV} from "../../utils/csvUtils";

/* ========== ~~~~~~~~~~ ACCOUNT SLICE : REDUCER ~~~~~~~~~~ ========== */
const initialState = {
  isLoading: false,
  revenueShareRequestStatus: 'idle',
  revenueShare: [],
  customRevenueShareRequestStatus: 'idle',
  customRevenueShare: [],
  invoiceConfigRequestStatus: 'idle',
  invoiceConfig: {
    showInvoices: false,
    enableVat: false,
    vatRate: null,
    vatRegistrationNumber: null,
    currencyCode: 'USD'
  },
  advancedUpdateStatus: 'idle',
  usageFeesUpdateStatus: 'idle',
  usageFeesUpdateStatusForAccount: 'idle',
  productRevenueDeduction: [],
  productRevenueDeductionForAccount: [],
  notificationsConfig: {},
  notificationsUpdateStatus: 'idle',
  reportApiKeyObject: {},
  reportApiKeyRequestStatus: 'idle',
  uploadBlockListCSVStatus: 'idle',
  getBlockListCSVStatus: 'idle',
  sspManagerUrlStatus: 'idle',
  adminPanelPermissions : 'idle',

  blockList: [],
  sspManagerUrl : '',
  sspPermissions: {},
}

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    updateCachedAccountRevenueShare: (state, action) => {
      const { index, revShareType, val } = action.payload
      state.revenueShare[index][revShareType] = val
    },
    updateCachedCustomRevenueShare: (state, action) => {
      state.customRevenueShare = action.payload
    },
    updateCachedInvoiceConfig: (state, action) => {
      state.invoiceConfig = action.payload
    },
    updateUpdateStatus: (state, action) => {
      const { field, status } = action.payload
      state[field] = status
    },
    updateCachedRevenueDeduction: (state, action) => {
      state.productRevenueDeduction = action.payload
    },
    updateCachedRevenueDeductionForAccount: (state, action) => {
      state.productRevenueDeductionForAccount = action.payload
    },
    updateCachedNotificationsConfig: (state, action) => {
      state.notificationsConfig = action.payload
    },
    resetToDB: (state, action) => {
      const { cache, db } = action.payload
      if (state[db]) state[cache] = state[db]
    }
  },
  extraReducers: builder => {
    builder
        .addCase(fetchAccountRevenueShare.pending, (state, action) => {
          state.revenueShareRequestStatus = 'pending'
        })
        .addCase(fetchAccountRevenueShare.fulfilled, (state, action) => {
          const mappedData = action.payload.map(revShare => ({
            ...revShare,
            initialLoadRevenueSharePercentage: (revShare.initialLoadRevenueSharePercentage * 100).toFixed(2),
            refreshRevenueSharePercentage: (revShare.refreshRevenueSharePercentage * 100).toFixed(2)
          }))
          state.revenueShare = mappedData
          state.revenueShareFromDB = mappedData
          state.revenueShareRequestStatus = 'idle'
        })
        .addCase(updateAccountRevenueShare.fulfilled, (state, action) => {
          state.advancedUpdateStatus = 'success'
        })
        .addCase(updateAccountRevenueShare.rejected, (state, action) => {
          state.advancedUpdateStatus = 'failed'
          state.revenueShare = state.revenueShareFromDB
        })
        .addCase(fetchSiteRevenueShare.pending, (state, action) => {
          state.customRevenueShareRequestStatus = 'pending'
        })
        .addCase(fetchSiteRevenueShare.fulfilled, (state, action) => {
          let uuidCounter = 0
          const mappedData = action.payload.map(revShare => ({
            ...revShare,
            id: Date.now() + uuidCounter++,
            initialLoadRevenueSharePercentage: (revShare.initialLoadRevenueSharePercentage * 100).toFixed(2),
            refreshRevenueSharePercentage: (revShare.refreshRevenueSharePercentage * 100).toFixed(2)
          }))
          state.customRevenueShare = mappedData
          state.customRevenueShareFromDB = mappedData
          state.customRevenueShareRequestStatus = 'idle'
        })
        .addCase(updateSiteRevenueShare.fulfilled, (state, action) => {
          state.advancedUpdateStatus = 'success'
        })
        .addCase(updateSiteRevenueShare.rejected, (state, action) => {
          state.advancedUpdateStatus = 'failed'
          state.customRevenueShare = state.customRevenueShareFromDB
        })
        .addCase(fetchInvoiceConfig.pending, (state, action) => {
          state.invoiceConfigRequestStatus = 'pending'
        })
        .addCase(fetchInvoiceConfig.fulfilled, (state, action) => {
          const {showInvoices, enableVat, vatRate, vatRegistrationNumber, currencyCode} = action.payload
          state.invoiceConfig.showInvoices = showInvoices
          state.invoiceConfig.enableVat = enableVat
          state.invoiceConfig.vatRate = (vatRate * 100).toFixed(2)
          state.invoiceConfig.vatRegistrationNumber = vatRegistrationNumber
          state.invoiceConfig.currencyCode = currencyCode
          state.invoiceConfigFromDB = state.invoiceConfig
          state.invoiceConfigRequestStatus = 'idle'
        })
        .addCase(updateInvoiceConfig.fulfilled, (state, action) => {
          const {showInvoices, enableVat, vatRate, vatRegistrationNumber, currencyCode} = action.payload
          state.invoiceConfig.showInvoices = showInvoices
          state.invoiceConfig.enableVat = enableVat
          state.invoiceConfig.vatRate = (vatRate * 100).toFixed(2)
          state.invoiceConfig.vatRegistrationNumber = vatRegistrationNumber
          state.invoiceConfig.currencyCode = currencyCode
          state.invoiceConfigFromDB = state.invoiceConfig
          state.advancedUpdateStatus = 'success'
        })
        .addCase(updateInvoiceConfig.rejected, (state, action) => {
          state.invoiceConfig = state.invoiceConfigFromDB
          state.advancedUpdateStatus = 'failed'
        })
        .addCase(fetchLatestProductRevenueDeduction.fulfilled, (state, action) => {
          Object.keys(action.payload).map(product => {
            action.payload[product] = (action.payload[product] * 100).toFixed(2)
          })
          state.productRevenueDeduction = action.payload
          state.productRevenueDeductionFromDB = action.payload
        })
        .addCase(fetchLatestProductRevenueDeductionForAccount.fulfilled, (state, action) => {
          Object.keys(action.payload).map(product => {
            action.payload[product] = (action.payload[product] * 100).toFixed(2)
          })
          state.productRevenueDeductionForAccount = action.payload
          state.productRevenueDeductionForAccountFromDB = action.payload
        })
        .addCase(updateProductRevenueDeduction.pending, (state, action) => {
          state.usageFeesUpdateStatus = 'pending'
        })
        .addCase(updateProductRevenueDeduction.fulfilled, (state, action) => {
          Object.keys(action.payload).map(product => {
            action.payload[product] = (action.payload[product] * 100).toFixed(2)
          })
          state.productRevenueDeduction = action.payload
          state.productRevenueDeductionFromDB = action.payload
          state.usageFeesUpdateStatus = 'success'
        })
        .addCase(updateProductRevenueDeduction.rejected, (state, action) => {
          state.usageFeesUpdateStatus = 'failed'
          state.productRevenueDeduction = state.productRevenueDeductionFromDB
        })
        .addCase(updateProductRevenueDeductionForAccount.pending, (state, action) => {
          state.usageFeesUpdateStatusForAccount = 'pending'
        })
        .addCase(updateProductRevenueDeductionForAccount.fulfilled, (state, action) => {
          Object.keys(action.payload).map(product => {
            action.payload[product] = (action.payload[product] * 100).toFixed(2)
          })
          state.productRevenueDeductionForAccount = action.payload
          state.productRevenueDeductionForAccountFromDB = action.payload
          state.usageFeesUpdateStatusForAccount = 'success'
        })
        .addCase(updateProductRevenueDeductionForAccount.rejected, (state, action) => {
          state.usageFeesUpdateStatusForAccount = 'failed'
          state.productRevenueDeductionForAccount = state.productRevenueDeductionForAccountFromDB
        })
        .addCase(fetchDashboardMessage.fulfilled, (state, action) => {
          state.notificationsConfig = action.payload
          state.notificationsConfigFromDB = action.payload
        })
        .addCase(updateDashboardMessage.pending, (state, action) => {
          state.notificationsUpdateStatus = 'pending'
        })
        .addCase(updateDashboardMessage.fulfilled, (state, action) => {
          state.notificationsConfig = action.payload
          state.notificationsConfigFromDB = action.payload
          state.notificationsUpdateStatus = 'success'
        })
        .addCase(updateDashboardMessage.rejected, (state, action) => {
          state.notificationsUpdateStatus = 'failed'
          state.notificationsConfig = state.notificationsConfigFromDB
        }).addCase(fetchReportApiKey.pending, (state, action) => {
          state.reportApiKeyRequestStatus = 'pending';
        }).addCase(fetchReportApiKey.rejected, (state, action) => {
          state.reportApiKeyRequestStatus = 'failed';
        }).addCase(fetchReportApiKey.fulfilled, (state, action) => {
          state.reportApiKeyObject = action.payload;
          state.reportApiKeyRequestStatus = 'idle';
        }).addCase(createReportApiKey.fulfilled, (state, action) => {
          state.reportApiKeyRequestStatus = 'success';
          state.reportApiKeyObject = action.payload;
        })
        .addCase(uploadBlockListCSV.pending, (state, action) => {
          state.uploadBlockListCSVStatus = 'pending'
        })
        .addCase(uploadBlockListCSV.fulfilled, (state, action) => {
          state.uploadBlockListCSVStatus = 'success'
        })
        .addCase(uploadBlockListCSV.rejected, (state, action) => {
          state.uploadBlockListCSVStatus = 'failed'
        })
        .addCase(getBlockListCSV.pending, (state, action) => {
          state.getBlockListCSVStatus = 'pending'
        })
        .addCase(getBlockListCSV.fulfilled, (state, action) => {
          state.getBlockListCSVStatus = 'success'
          // get csv file data and parse it
          const parsedData = parseCSV(action.payload);
          state.blockList = parsedData;
        })
        .addCase(getBlockListCSV.rejected, (state, action) => {
          state.getBlockListCSVStatus = 'failed'
        })
  }
})

export const { updateCachedAccountRevenueShare, updateCachedCustomRevenueShare,
  updateCachedInvoiceConfig, updateUpdateStatus, updateCachedRevenueDeduction, updateCachedRevenueDeductionForAccount, updateCachedNotificationsConfig, resetToDB } = settingsSlice.actions
export default settingsSlice.reducer
